import {
    IProps,
    IRef,
} from './types';

import {
    Decimal,
} from 'decimal.js';
import React, {
    forwardRef,
    useImperativeHandle,
    useState,
} from 'react';

import {
    useInputMask,
} from 'tools/hooks';

import StringInput from '../StringInput';

import {
    ERROR,
} from './constants';
import {
    checkPositive,
    checkValue,
    formatError,
    formatInitialValue,
    formatValue,
    getMask,
} from './functions';

const DecimalInput = forwardRef<IRef, IProps>((props, ref) => {
    const [decimalValue, setDecimalValue] = useState(props.value);
    const [error, setError] = useState<ERROR | null>(null);

    const {value, setValue, inputProps} = useInputMask({
        value: formatInitialValue(props.value),
        mask: getMask(props.numeric.precision),
        shouldChange(value: string): boolean {
            return checkValue(value, props.numeric.precision, props.numeric.scale, !!props.withNegative);
        },
        onChange(value: string) {
            let formattedValue = formatValue(value);

            if (formattedValue !== null) {
                if (props.min !== undefined) {
                    if (new Decimal(formattedValue).lessThan(props.min)) {
                        formattedValue = formatInitialValue(props.min);
                        setValue(formattedValue);
                    }
                }
                if (props.max !== undefined) {
                    if (new Decimal(formattedValue).greaterThan(props.max)) {
                        formattedValue = formatInitialValue(props.max);
                        setValue(formattedValue);
                    }
                }
            }

            setDecimalValue(formattedValue);
            setError(null);

            props.onChangeValue?.(formattedValue);
        },
    });

    useImperativeHandle(ref, () => {
        return {
            getValue(): string | null {
                return decimalValue;
            },
            setValue(value: string | null) {
                setDecimalValue(value);
                setValue(formatInitialValue(value));
            },
            validate(): boolean {
                if (decimalValue === null) {
                    if (props.isRequired) {
                        setError(ERROR.IS_REQUIRED);

                        return false;
                    }
                } else if (props.isPositive && !checkPositive(decimalValue)) {
                    setError(ERROR.NOT_POSITIVE);

                    return false;
                }

                setError(null);

                return true;
            },
            setError(error: ERROR): void {
                setError(error);
            },
        };
    });

    return (
        <StringInput
            className={props.className}
            value={value}
            size={props.size}
            label={props.label}
            placeholder={props.placeholder}
            isDisabled={props.isDisabled}
            isError={!!error || props.isError}
            errorMessage={formatError(error) || props.errorMessage}
            {...inputProps}
        />
    );
});

export default DecimalInput;
